import { useRef, type ReactNode } from 'react';
import {
  ImperativeContext,
  type CanvasImperative,
} from './imperative-context';

interface ImperativeProviderProps {
  children: ReactNode;
}

/**
 * Mounts above both the canvas engine AND any portal-sibling panels
 * (Drawers, popovers) that need imperative access to the canvas.
 *
 * Pattern:
 *
 *     <ImperativeProvider>
 *       <CanvasEngine />            // mounts <Imperative /> inside its RF tree
 *       <Drawer><FramesPanel /></Drawer>  // calls useImperative() in the portal
 *     </ImperativeProvider>
 *
 * The provider owns a ref. The Imperative bridge writes to it on
 * mount; consumers read from it via useImperative. Single ref, single
 * source of truth — adding new portal-sibling consumers is just
 * `useImperative()`.
 */
export function ImperativeProvider({ children }: ImperativeProviderProps) {
  const ref = useRef<CanvasImperative | null>(null);
  return (
    <ImperativeContext.Provider value={ref}>
      {children}
    </ImperativeContext.Provider>
  );
}
